import * as authTokenStore from "./authTokenStore.js";
import * as userInfo from "./userInfo.js";

function nonEmptyIdentityPart(value) {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    if (!normalized || normalized === "default") return null;
    return normalized;
}

function encodeIdentityPart(value) {
    return encodeURIComponent(value);
}

/**
 * Return the only browser-persistence owner accepted by the dashboard.
 * Both values come from the authenticated `/v1/me` result. Before that
 * result exists, or when a caller asks for a different tenant, persistence
 * is unavailable rather than falling back to a shared bucket.
 */
export function currentBrowserStorageOwner(requestedTenantId = null) {
    let currentTenantId = null;
    let currentUserId = null;
    try { currentTenantId = userInfo.getCurrentTenantIdSync?.(); } catch { /* incomplete unit-test mock */ }
    try { currentUserId = userInfo.getCurrentUserIdSync?.(); } catch { /* incomplete unit-test mock */ }
    const tenantId = nonEmptyIdentityPart(currentTenantId);
    const userId = nonEmptyIdentityPart(currentUserId);
    const requested = requestedTenantId == null
        ? tenantId
        : nonEmptyIdentityPart(requestedTenantId);
    if (!tenantId || !userId || !requested || requested !== tenantId) return null;
    return Object.freeze({
        tenantId,
        userId,
        identityEpoch: currentIdentityEpoch(),
        key: `${encodeIdentityPart(tenantId)}:${encodeIdentityPart(userId)}`,
    });
}

export function browserStorageOwnerIsCurrent(owner) {
    if (!owner || owner.identityEpoch !== currentIdentityEpoch()) return false;
    const current = currentBrowserStorageOwner(owner.tenantId);
    return current != null && current.key === owner.key;
}

export function browserStorageKey(prefix, requestedTenantId = null) {
    const owner = currentBrowserStorageOwner(requestedTenantId);
    return owner == null ? null : `${prefix}:${owner.key}`;
}

export function requireBrowserStorageOwner(requestedTenantId = null) {
    const owner = currentBrowserStorageOwner(requestedTenantId);
    if (owner == null) {
        throw new Error("verified tenant and user identity are required for browser persistence");
    }
    return owner;
}

// A tenant-only entry cannot prove which user created it. Never migrate it.
export function discardLegacyTenantEntry(storage, prefix, requestedTenantId = null) {
    let currentTenantId = null;
    try { currentTenantId = userInfo.getCurrentTenantIdSync?.(); } catch { /* incomplete unit-test mock */ }
    const tenantId = nonEmptyIdentityPart(requestedTenantId ?? currentTenantId);
    if (!storage || !tenantId) return;
    try { storage.removeItem(`${prefix}:${tenantId}`); } catch { /* storage unavailable */ }
    try { storage.removeItem(`${prefix}:default`); } catch { /* storage unavailable */ }
}

function currentIdentityEpoch() {
    try { return Number(authTokenStore.getAuthIdentityEpoch?.()) || 0; }
    catch { return 0; }
}
